import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { moonpayApi, type MoonpayKycState } from '../api/moonpay.api';

import { MOONPAY_ENABLED } from '@/constants';

export const MOONPAY_QUERY_KEYS = {
  all: ['moonpay'] as const,
  config: () => [...MOONPAY_QUERY_KEYS.all, 'config'] as const,
  customer: () => [...MOONPAY_QUERY_KEYS.all, 'customer'] as const,
};

/** Non-secret client config (publishable key + mode). */
export function useMoonpayConfig(enabled: boolean = MOONPAY_ENABLED) {
  return useQuery({
    queryKey: MOONPAY_QUERY_KEYS.config(),
    queryFn: moonpayApi.getConfig,
    staleTime: 60 * 60 * 1000,
    enabled,
  });
}

/**
 * Current KYC state. Polls while MoonPay is verifying so the gate advances to
 * `active` without a manual refresh.
 */
export function useMoonpayCustomer(opts: { enabled?: boolean; poll?: boolean } = {}) {
  const { enabled = true, poll = false } = opts;
  return useQuery<MoonpayKycState>({
    queryKey: MOONPAY_QUERY_KEYS.customer(),
    queryFn: moonpayApi.getCustomer,
    enabled: enabled && MOONPAY_ENABLED,
    refetchInterval: poll ? 4000 : false,
    staleTime: 2000,
  });
}

export function useLinkMoonpayCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) => moonpayApi.linkCustomer(customerId),
    onSuccess: (data) => qc.setQueryData(MOONPAY_QUERY_KEYS.customer(), data),
  });
}

export function useSubmitMoonpayKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, any>) => moonpayApi.submitKyc(payload),
    onSuccess: (data) => qc.setQueryData(MOONPAY_QUERY_KEYS.customer(), data),
  });
}
